import React, { useCallback, useContext } from 'react'

import CardContainer from './Common/CardContainer'
import TopContent from './Common/TopContent'
import { ItemCardVariant } from './ItemCard'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import EditTripItem from '../TripModal/TripItems/Edit'
import HeadingFullPrint from '../util/HeadingFullPrint'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LinePaperclipIcon from 'components/Luxkit/Icons/line/LinePaperclipIcon'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import Group from 'components/utils/Group'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import noop from 'lib/function/noop'
import AttachedDocument from 'tripPlanner/components/Documents/AttachedDocument'
import BaseItemCard from 'tripPlanner/components/ItemCard/BaseItemCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { NoteItem } from 'tripPlanner/types/tripItem'
import { formatDateTimeOrDateRange } from 'tripPlanner/utils'

interface Props {
  item: NoteItem
  onClickTripName?: () => void
  variant: ItemCardVariant
}

function NoteItemCard({ item, onClickTripName = noop, variant }: Props) {
  const { notes, link, title } = item

  const timeFormat = useRegionTimeFormat()
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const dateTimeLabel = formatDateTimeOrDateRange(item, timeFormat)

  const openModal = useOpenModal()

  const editItem = useCallback(
    () => openModal(EditTripItem, { item }),
    [item, openModal],
  )

  const documentContext = useContext(TripDocumentsContext)
  const attachedDocuments =
    documentContext?.documentsResult?.attachedFiles[item.id] ?? []

  return (
    <BaseItemCard item={item} alwaysShowModal>
      <CardContainer>
        <Group direction="vertical" gap={8} fullWidth>
          <TopContent item={item} variant={variant}>
            Note
          </TopContent>
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              {title && (
                <HeadingFullPrint lineClamp={2} variant="heading6">
                  {title}
                </HeadingFullPrint>
              )}
              {notes && (
                <MarkdownRender
                  type="compact"
                  fontSize="large"
                  content={`${notes}${link ? `\n\n${link}` : ''}`}
                />
              )}
            </VerticalSpacer>
            {dateTimeLabel && (
              <Group direction="horizontal">
                <BodyTextBlock variant="medium" weight="normal">
                  {dateTimeLabel}
                </BodyTextBlock>
                {variant === 'tripSummary' && (
                  <OutsideTripDatesIcon
                    item={item}
                    changeItemDates={editItem}
                  />
                )}
              </Group>
            )}
            {attachedDocuments.map((document) => (
              <AttachedDocument key={document.filename} document={document} />
            ))}
            {documentContext && canEdit && (
              <AssistChip
                size="small"
                onClick={() => documentContext.onRequestAddDocument(item.id)}
                variant="filled"
                startIcon={<LinePaperclipIcon />}
              >
                Attach document
              </AssistChip>
            )}
          </VerticalSpacer>
          {variant === 'drawer' && tripContext && (
            <StructureLabel variant="information">
              <BodyTextBlock variant="small">Added to </BodyTextBlock>
              <TextLink
                size="caption"
                onClick={(e) => {
                  e.stopPropagation()
                  onClickTripName()
                }}
              >
                {tripContext.name}
              </TextLink>
            </StructureLabel>
          )}
        </Group>
      </CardContainer>
    </BaseItemCard>
  )
}

export default NoteItemCard
